import { useState } from 'react';
import { Mail, Phone, MapPin, Send, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const BG_IMAGE = "https://images.unsplash.com/photo-1579003223313-8053dc9d10aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxNYWtrYWglMjBLYWFiYSUyMG5pZ2h0JTIwdmlldyUyMGhpZ2glMjBxdWFsaXR5fGVufDB8fHx8MTc3MDU0MDA3OHww&ixlib=rb-4.1.0&q=85";

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { API } = useAuth();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/suggestions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error('Failed to send message');

      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      label: 'WhatsApp',
      value: '+254 707 610 099 or +254 710 215 839',
      color: 'bg-green-500/20 text-green-500',
    },
    {
      icon: Mail,
      label: 'Email Us',
      value: 'info.akit001@gmail.com',
      color: 'bg-primary/20 text-primary',
    },
    {
      icon: MapPin,
      label: 'Visit Us',
      value: 'AKIT Plaza, Nairobi, Kenya',
      color: 'bg-orange-500/20 text-orange-500',
    },
  ];

  return (
    <div 
      className="min-h-screen pb-20"
      style={{ 
        backgroundImage: `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.8)), url(${BG_IMAGE})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Header */}
      <div className="py-12 text-center">
        <p className="text-primary font-amiri text-2xl mb-2">بِسْمِ اللهِ الرَّحْمٰنِ الرَّحِيْمِ</p>
        <p className="text-lg text-foreground/90 font-amiri">السَّلَامُ عَلَيْكُمْ</p>
      </div>

      <div className="container mx-auto px-4 md:px-8 max-w-4xl">
        {/* Contact Form Card */}
        <div className="glass-card-heavy rounded-2xl p-8 mb-8">
          {submitted ? (
            <div className="text-center py-8">
              <div className="h-16 w-16 rounded-full bg-primary/20 mx-auto flex items-center justify-center mb-6">
                <CheckCircle className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-foreground mb-4">Message Sent!</h2>
              <p className="text-muted-foreground mb-6">
                Thank you for reaching out. We'll get back to you as soon as possible.
              </p>
              <Button 
                onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', subject: '', message: '' }); }}
                className="btn-primary"
              >
                Send Another Message
              </Button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-heading font-bold text-foreground mb-2">Contact Us</h1>
              <p className="text-muted-foreground mb-6">
                Reach out to the AKIT team for any inquiries or support.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground ml-1">
                    Your Name
                  </label>
                  <Input
                    type="text"
                    name="name"
                    placeholder="Abdullah Hassan"
                    value={formData.name}
                    onChange={handleChange}
                    className="input-glass h-12"
                    required
                    data-testid="contact-name"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground ml-1">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    name="email"
                    placeholder="abdullah@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="input-glass h-12"
                    required
                    data-testid="contact-email"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground ml-1">
                    Subject
                  </label>
                  <Input
                    type="text"
                    name="subject"
                    placeholder="General Inquiry"
                    value={formData.subject}
                    onChange={handleChange}
                    className="input-glass h-12"
                    required
                    data-testid="contact-subject"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground ml-1">
                    Your Message
                  </label>
                  <textarea
                    name="message"
                    placeholder="How can we help you today?"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="input-glass w-full py-3 resize-none"
                    required
                    data-testid="contact-message"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-primary h-14 text-lg"
                  disabled={loading}
                  data-testid="contact-submit"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </form>
            </>
          )}
        </div>

        {/* Contact Details */}
        <div className="grid gap-4">
          {contactInfo.map((info, index) => (
            <div 
              key={index}
              className="glass-card rounded-xl p-4 flex items-center gap-4"
              data-testid={`contact-info-${index}`}
            >
              <div className={`h-10 w-10 rounded-full flex items-center justify-center ${info.color}`}>
                <info.icon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase font-bold">{info.label}</p>
                <p className="text-sm font-semibold text-foreground">{info.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer Note */}
        <p className="text-center text-muted-foreground/50 text-xs mt-12">
          © {new Date().getFullYear()} AKIT Network Kenya. All Rights Reserved.
        </p>
      </div>
    </div>
  );
};
